import { X, Trash2, ChevronLeft, ChevronRight, Edit2, Check } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import nimonikLogo from '../../assets/images/nimonik-logo.png'
import { ConversationSummary } from '@/api/conversationService'
import FilterPanel, { FilterPanelProps } from './FilterPanel'

interface SidebarProps extends FilterPanelProps {
  isOpen: boolean
  onClose: () => void
  onNewChat: () => void
  conversations: ConversationSummary[]
  isCollapsed: boolean
  onToggleCollapse: () => void
  onLoadConversation: (conversationId: number) => void
  currentConversationId: number | null
  onRenameConversation: (conversationId: number, newTitle: string) => void
  onDeleteConversation: (conversationId: number) => void
}

export default function Sidebar({
  isOpen,
  onClose,
  onNewChat,
  conversations,
  isCollapsed,
  onToggleCollapse,
  onLoadConversation,
  currentConversationId,
  onRenameConversation,
  onDeleteConversation,
  // Filter props
  selectedJurisdictionUuids,
  setSelectedJurisdictionUuids,
  selectedPubAuthorityUuids,
  setSelectedPubAuthorityUuids,
  selectedDocumentLegislationUuids,
  setSelectedDocumentLegislationUuids,
  jurisdictionError,
  onJurisdictionErrorDismiss,
  onDocumentMapUpdate,
}: SidebarProps) {
  const { t } = useTranslation()
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editTitle, setEditTitle] = useState('')

  const handleStartEdit = (conv: ConversationSummary, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingId(conv.conversation_id)
    setEditTitle(conv.title || '')
  }

  const handleSaveEdit = (conversationId: number) => {
    const trimmedTitle = editTitle.trim()
    if (trimmedTitle) {
      onRenameConversation(conversationId, trimmedTitle)
    }
    setEditingId(null)
    setEditTitle('')
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditTitle('')
  }

  const handleKeyDown = (e: React.KeyboardEvent, conversationId: number) => {
    if (e.key === 'Enter') {
      handleSaveEdit(conversationId)
    } else if (e.key === 'Escape') {
      handleCancelEdit()
    }
  }

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:relative inset-y-0 left-0 z-50
          bg-gray-50
          border-r border-gray-100
          transform transition-all duration-300 ease-in-out
          flex flex-col
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isCollapsed ? 'w-16' : 'w-64 lg:w-72'}
        `}
      >
        {/* Mobile Close Button */}
        <div className="lg:hidden p-4 flex justify-end">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Top Section: New Chat Button & Collapse Toggle */}
        <div className={`border-b border-gray-100 bg-gray-50/50 ${isCollapsed ? 'px-3 py-4' : 'p-4'}`}>
          <div className="flex items-center gap-2">
            {/* New Chat Button */}
            <button
              onClick={onNewChat}
              className={`bg-white border border-brand-100 text-brand shadow-sm py-2 rounded-md flex items-center justify-center transition-all group font-medium hover:shadow-md ${isCollapsed ? 'p-2' : 'flex-1 px-3'
                }`}
              title={t('sidebar.newChat')}
            >
              <img
                src={nimonikLogo}
                alt="Nimonik Logo"
                className={`rounded object-contain transition-all duration-300 flex-shrink-0 ${isCollapsed ? 'w-6 h-6' : 'w-5 h-5 mr-2'
                  }`}
              />
              {!isCollapsed && (
                <span className="whitespace-nowrap">
                  {t('sidebar.newChat')}
                </span>
              )}
            </button>

            {/* Collapse Toggle - Desktop only */}
            {!isCollapsed && (
              <button
                onClick={onToggleCollapse}
                className="hidden lg:block p-2 hover:bg-gray-200 rounded transition-colors"
                title={t('sidebar.collapseSidebar')}
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
            )}

            {/* Expand Toggle - When collapsed */}
            {isCollapsed && (
              <button
                onClick={onToggleCollapse}
                className="hidden lg:block p-2 hover:bg-gray-200 rounded transition-colors" style={{ marginLeft: '3px' }}
                title={t('sidebar.expandSidebar')}
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            )}
          </div>
        </div>

        {/* Scrollable area: Filters + Recent Chats */}
        {!isCollapsed && (
          <div className="flex-1 overflow-y-auto">
            {/* Filter Panel */}
            <div className="p-4 border-b border-gray-100">
              <FilterPanel
                selectedJurisdictionUuids={selectedJurisdictionUuids}
                setSelectedJurisdictionUuids={setSelectedJurisdictionUuids}
                selectedPubAuthorityUuids={selectedPubAuthorityUuids}
                setSelectedPubAuthorityUuids={setSelectedPubAuthorityUuids}
                selectedDocumentLegislationUuids={selectedDocumentLegislationUuids}
                setSelectedDocumentLegislationUuids={setSelectedDocumentLegislationUuids}
                jurisdictionError={jurisdictionError}
                onJurisdictionErrorDismiss={onJurisdictionErrorDismiss}
                onDocumentMapUpdate={onDocumentMapUpdate}
              />
            </div>

            {/* Recent Chats */}
            <div className="p-3">
              {conversations.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-xs text-gray-400">{t('sidebar.noHistory')}</p>
                </div>
              ) : (
                <div className="mb-4">
                  <h3 className="px-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                    {t('sidebar.recentChats')}
                  </h3>
                  <ul className="space-y-1">
                    {conversations.map((conv) => (
                      <li
                        key={conv.conversation_id}
                        className={`group flex items-center justify-between px-3 py-2 rounded-md hover:bg-gray-100 transition-colors relative ${editingId === conv.conversation_id ? '' : 'cursor-pointer'
                          } ${conv.conversation_id === currentConversationId ? 'bg-brand-50 border-l-2 border-brand' : ''}`}
                        onClick={() => editingId !== conv.conversation_id && onLoadConversation(conv.conversation_id)}
                      >
                        {editingId === conv.conversation_id ? (
                          <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, conv.conversation_id)}
                              onBlur={() => handleSaveEdit(conv.conversation_id)}
                              className="flex-1 text-xs px-2 py-1 border border-brand rounded focus:outline-none focus:ring-1 focus:ring-brand"
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveEdit(conv.conversation_id)}
                              className="p-1 text-brand hover:text-brand-hover transition-colors"
                              title={t('sidebar.save')}
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span className={`text-xs truncate flex-1 ${conv.conversation_id === currentConversationId ? 'text-brand font-semibold' : 'text-gray-600'
                              }`}>
                              {conv.title || t('sidebar.untitledConversation')}
                            </span>
                            {/* Hover Actions */}
                            <div className="hidden group-hover:flex items-center space-x-1 absolute right-2 bg-gray-100 pl-2">
                              <button
                                title={t('sidebar.rename')}
                                className="p-1 text-gray-400 hover:text-brand transition-colors"
                                onClick={(e) => handleStartEdit(conv, e)}
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                title={t('sidebar.deleteChat')}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onDeleteConversation(conv.conversation_id)
                                }}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </aside>
    </>
  )
}
